import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { analyticsReady, trackPageview } from "@/lib/analytics";

/**
 * Fires an Umami pageview on the initial load and on every client-side route
 * change. Umami's built-in auto-tracking is disabled (see the loader in
 * index.html) so this component is the single source of pageviews — no
 * double-counting of SPA navigations.
 *
 * The Umami script loads async, so on the very first render `window.umami` may
 * not be ready yet; we retry briefly until it is. Renders nothing; a complete
 * no-op when analytics isn't configured. Mounted once, site-wide, in App.tsx.
 */
export function Analytics() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    const url = `${pathname}${search}`;

    if (analyticsReady()) {
      trackPageview(url);
      return;
    }

    // Tracker not loaded yet (async script on first paint) — poll briefly.
    let tries = 0;
    const timer = setInterval(() => {
      if (analyticsReady()) {
        trackPageview(url);
        clearInterval(timer);
      } else if (++tries >= 40) {
        // ~10s: analytics is unconfigured or blocked; give up quietly.
        clearInterval(timer);
      }
    }, 250);
    return () => clearInterval(timer);
  }, [pathname, search]);

  return null;
}

export default Analytics;
